import '../../styles/guide.css';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Fingerprint, HardDrives, Key, ListChecks, Scroll, Wrench } from '@phosphor-icons/react';

import { Widget } from '../blog/Figure';

/**
 * Three figures for /blog/best-mcp-gateways.
 *
 * GatewayCriteria: the six questions the post says to ask any gateway, as an
 * icon grid. It shows the structure of the guide so a skimmer gets the list.
 * GatewayChooser: tick what you need and see which of the three documented
 * gateways match. Every cell is from the vendor's own public page or docs, as
 * read on 2026-10-10, hosting and OpenAPI rows on 2026-10-12 (sources in the post); "not checked" means the sources I
 * read do not say, and it is never counted as a match.
 * TeamCostCompare: list prices for TrueFoundry Pro and Statio by team size, so
 * the crossover is a number you can move rather than a claim.
 */

// ─── 1. The six questions ───────────────────────────────────────────────────

const CRITERIA: { icon: ReactNode; q: string; ask: string }[] = [
  {
    icon: <Wrench size={22} />,
    q: 'Access granularity',
    ask: 'Can one agent get the read tool and not the delete tool on the same server?',
  },
  {
    icon: <Fingerprint size={22} />,
    q: 'Agent identity',
    ask: 'Does each agent have its own name, token and off switch?',
  },
  {
    icon: <Key size={22} />,
    q: 'Credentials',
    ask: 'Where does the upstream API key live, and can anyone read it back?',
  },
  { icon: <HardDrives size={22} />, q: 'Where it runs', ask: 'Managed, or in your own cloud, and on which plan?' },
  { icon: <Scroll size={22} />, q: 'Audit trail', ask: 'Are refused calls recorded, and can the log reach your SIEM?' },
  { icon: <ListChecks size={22} />, q: 'Price shape', ask: 'Per user, per call or flat, and is it published?' },
];

export function GatewayCriteria() {
  return (
    <Widget title="Six questions to put to any MCP gateway">
      <ul className="bw-in gc-grid">
        {CRITERIA.map((c) => (
          <li key={c.q}>
            <span className="gc-ico" aria-hidden="true">
              {c.icon}
            </span>
            <div>
              <b>{c.q}</b>
              <p>{c.ask}</p>
            </div>
          </li>
        ))}
      </ul>
    </Widget>
  );
}

// ─── 2. Tick what you need ──────────────────────────────────────────────────

type Cell = boolean | null;
const GATEWAYS = ['Runlayer', 'TrueFoundry', 'Statio'] as const;

/** One row per requirement; cells are Runlayer, TrueFoundry, Statio. null = not stated in the sources read. */
const NEEDS: { id: string; label: string; cells: [Cell, Cell, Cell] }[] = [
  { id: 'tool', label: 'Access rules down to a single tool', cells: [true, true, true] },
  { id: 'args', label: 'Rules that look at tool arguments', cells: [true, null, false] },
  { id: 'price', label: 'Prices published on the website', cells: [false, true, true] },
  { id: 'flat', label: 'One flat price, not per user', cells: [null, false, true] },
  { id: 'host', label: 'Runs in your own cloud', cells: [null, true, true] },
  { id: 'hosts', label: 'Runs MCP servers for you', cells: [true, true, true] },
  { id: 'openapi', label: 'Builds a server from an OpenAPI spec', cells: [null, true, true] },
  { id: 'llm', label: 'LLM gateway in the same product', cells: [null, true, false] },
];

export function GatewayChooser() {
  const [picked, setPicked] = useState<string[]>(['tool', 'flat']);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const chosen = NEEDS.filter((n) => picked.includes(n.id));

  return (
    <Widget title="Which of these meets what you need?">
      <div className="bw-in bw-cols">
        <fieldset className="gc-needs">
          <legend className="bw-note">Tick your requirements</legend>
          {NEEDS.map((n) => (
            <label key={n.id}>
              <input type="checkbox" checked={picked.includes(n.id)} onChange={() => toggle(n.id)} />
              <span>{n.label}</span>
            </label>
          ))}
        </fieldset>
        <table className="bw-table" aria-live="polite">
          <thead>
            <tr>
              <th scope="col">Gateway</th>
              <th scope="col">Meets</th>
              <th scope="col">Not met or not checked</th>
            </tr>
          </thead>
          <tbody>
            {GATEWAYS.map((g, i) => {
              const met = chosen.filter((n) => n.cells[i] === true).length;
              const no = chosen.filter((n) => n.cells[i] === false).length;
              const unknown = chosen.length - met - no;
              return (
                <tr key={g}>
                  <th scope="row">{g}</th>
                  <td className="big">{chosen.length ? `${met} of ${chosen.length}` : 'none ticked'}</td>
                  <td>{chosen.length ? `${no} not met, ${unknown} not checked` : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Widget>
  );
}

// ─── 3. What a month costs ──────────────────────────────────────────────────

const TF_PRO_PER_USER = 25;
const STATIO_TEAM = 149;
const STATIO_BUSINESS = 599;
const TEAM_MAX = 15;

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

export function TeamCostCompare() {
  const [people, setPeople] = useState(10);
  const [sso, setSso] = useState(false);

  const tf = people * TF_PRO_PER_USER;
  const statio = sso ? STATIO_BUSINESS : STATIO_TEAM;
  const top = Math.max(TEAM_MAX * TF_PRO_PER_USER, STATIO_BUSINESS);
  const cheaper = tf < statio ? 'TrueFoundry Pro' : tf > statio ? (sso ? 'Statio Business' : 'Statio Team') : 'neither';

  return (
    <Widget title="A month of list price, by team size">
      <div className="bw-in">
        <div className="bw-range">
          <label htmlFor="tcc-people">People on the team</label>
          <input
            id="tcc-people"
            type="range"
            min={1}
            max={TEAM_MAX}
            value={people}
            onChange={(e) => setPeople(Number(e.target.value))}
          />
          <output htmlFor="tcc-people">{people}</output>
        </div>
        <label className="gc-check">
          <input type="checkbox" checked={sso} onChange={() => setSso(!sso)} />
          <span>I need SSO</span>
        </label>
      </div>
      <div className="bw-in" aria-live="polite">
        <div className="bw-bar">
          <span>TrueFoundry Pro</span>
          <span className="track">
            <span className="fill light" style={{ width: `${(tf / top) * 100}%` }} />
          </span>
          <span>{usd(tf)}</span>
        </div>
        <div className="bw-bar">
          <span>{sso ? 'Statio Business' : 'Statio Team'}</span>
          <span className="track">
            <span className="fill" style={{ width: `${(statio / top) * 100}%` }} />
          </span>
          <span>{usd(statio)}</span>
        </div>
        <p className="bw-note" style={{ marginTop: 12 }}>
          At {people} {people === 1 ? 'person' : 'people'}
          {sso ? ' with SSO' : ''}, {cheaper === 'neither' ? 'the two cost the same.' : `${cheaper} costs less.`}
        </p>
      </div>
    </Widget>
  );
}
